import { combineReducers, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { LocalStorage } from '@vectord/utils';

import { bulkUpdateLeadsSlice } from './bulkUpdateLeads';
import { exportLeadsSlice } from './exportLeads';
import { importLeadSlice } from './importLeads';
import { manageLeadsSlice } from './manageLeadsPage';
import { quickFilterSlice } from './quickFilter';
import { secondaryFilterSlice } from './secondaryFilter';
import { selectLeadAttributesModalSlice } from './selectLeadAttributesModal';

// Keys to exclude from localStorage for each slice
const STORAGE_EXCLUDE_KEYS = {
  manageLeads: ['isAdvancedSearchOpen', 'isEmailOpen', 'page'],
  secondaryFilter: [],
  quickFilter: ['reorderConfig', 'treeData'],
} as const;

// Action type for loading stored state
export const INITIALIZE_WITH_STORED_STATE = 'manageLeads/loadStoredState';

// Helper to remove excluded keys from state
const removeExcludedKeys = (state: any, sliceName: keyof typeof STORAGE_EXCLUDE_KEYS) => {
  const excludeKeys = STORAGE_EXCLUDE_KEYS[sliceName] || [];
  const filteredState = { ...state };
  excludeKeys.forEach((key) => {
    delete filteredState[key];
  });
  return filteredState;
};

// Create a slice for managing entityKey
export const entityKeySlice = createSlice({
  name: 'manageLeads/entityKey',
  initialState: null as string | null,
  reducers: {
    setEntityKey: (_, action: PayloadAction<string | null>) => action.payload,
  },
});

export const manageLeadReducers = combineReducers({
  entityKey: entityKeySlice.reducer,
  manageLeads: manageLeadsSlice.reducer,
  selectLeadAttributesModal: selectLeadAttributesModalSlice.reducer,
  secondaryFilter: secondaryFilterSlice.reducer,
  quickFilter: quickFilterSlice.reducer,
  importLeads: importLeadSlice.reducer,
  exportLeads: exportLeadsSlice.reducer,
  bulkUpdateLeads: bulkUpdateLeadsSlice.reducer,
});

// Create a middleware to handle localStorage
export const createEntityStorageMiddleware = () => (store: any) => (next: any) => (action: any) => {
  const result = next(action);

  // Handle entityKey changes
  if (action.type === entityKeySlice.actions.setEntityKey.type) {
    const entityKey = action.payload;
    if (entityKey) {
      const storageInstance = LocalStorage.getInstance(`store.manageLeads.${entityKey}`);
      const storedState = storageInstance?.config;

      if (storedState) {
        // Dispatch stored state to all slices
        store.dispatch({
          type: INITIALIZE_WITH_STORED_STATE,
          payload: storedState,
        });
      }
    }
  }

  // Handle saving state changes
  const { entityKey } = store.getState().manageLeads;
  if (entityKey && action.type.startsWith('manageLeads/')) {
    const state = store.getState().manageLeads;
    const storageInstance = LocalStorage.getInstance(`store.manageLeads.${entityKey}`);
    if (storageInstance) {
      const { entityKey: omittedKey, ...stateToStore } = state;

      // Filter out excluded keys for each slice before storing
      const filteredState = {
        manageLeads: removeExcludedKeys(stateToStore.manageLeads, 'manageLeads'),
        secondaryFilter: removeExcludedKeys(stateToStore.secondaryFilter, 'secondaryFilter'),
        quickFilter: removeExcludedKeys(stateToStore.quickFilter, 'quickFilter'),
      };

      storageInstance.config = filteredState;
    }
  }

  return result;
};

export const { setEntityKey } = entityKeySlice.actions;
